// Symptom #6 — bridge-connect popup honesty.
//
// Pure decision logic for the ProxyWatchdog "unable to connect" toast,
// extracted from watchdog.tsx so the suppression rule is unit-testable without
// mounting the React tree or mocking the watchdog's many dependencies.
// Per rule think-in-systems (ai.knowledge/global): keep the honesty gate a
// small, dependency-free module with a single responsibility.

/**
 * Extended grace before the "unable to connect" error TOAST may surface.
 * Deliberately longer than the analytics grace (STARTUP_GRACE_PERIOD_MS, 10s):
 * a healthy-but-slow boot (delayed router / NATS, e.g. ROUTER_DELAY_START_MS)
 * can take well over 10s to deliver the first proxy message. Showing the toast
 * at 10s of pre-link silence is a false positive — the local bridge is still
 * linking, not genuinely unreachable.
 */
export const OFFLINE_TOAST_GRACE_PERIOD_MS = 30_000;

/** Decision for whether the bridge-offline toast is honest right now. */
export type OfflineToastDecision = { kind: 'show' } | { kind: 'defer'; deferMs: number };

/**
 * Honesty gate for the bridge "unable to connect" toast.
 *
 * - A bridge that was ONLINE and dropped is a genuine disconnect → show now.
 * - A bridge that never connected but is still inside the extended boot grace
 *   is merely pre-link silence → defer; the caller re-checks after `deferMs`
 *   and only shows if it is still offline.
 * - A bridge that never connected and is past the grace is a genuine
 *   never-connect → show now.
 */
export const decideOfflineToast = (args: {
  hasEverConnected: boolean;
  timeSinceStartupMs: number;
  graceMs?: number;
}): OfflineToastDecision => {
  const graceMs = args.graceMs ?? OFFLINE_TOAST_GRACE_PERIOD_MS;
  if (args.hasEverConnected) {
    return { kind: 'show' };
  }
  if (args.timeSinceStartupMs < graceMs) {
    return { kind: 'defer', deferMs: graceMs - args.timeSinceStartupMs };
  }
  return { kind: 'show' };
};
